import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Award,
  ChevronDown,
  Copy,
  Gift,
  MessageCircle,
  Trophy,
  Wallet,
} from 'lucide-react';
import PartnerKitIcon from '../components/common/PartnerKitIcon';
import NewtonsCradleLoader from '../components/common/NewtonsCradleLoader';
import { getThemeColor } from '../config/appConfig';
import apiService, { extractClientsList } from '../utils/apiService';
import { useFetchOnceByKey } from '../hooks/useMountOnce';
import {
  formatGrowth,
  parseDashboardWidgets,
  toAmount,
} from '../utils/dashboardWidgets';
import { parseEarningOverview } from '../utils/earningOverview';
import { parsePerformanceOverview } from '../utils/performanceOverview';
import { parseLoyaltySummary } from '../utils/loyaltyData';
import DashboardAccountManager from './dashboard/DashboardAccountManager';
import DashboardTraders from './dashboard/DashboardTraders';
import {
  DUMMY_ACCOUNT_MANAGER,
  mapClientsToTraders,
} from './dashboard/dashboardSideData';
import './dashboard/dashboard-module.css';

const THEME = getThemeColor('primary');
const REFERRALS_BLUE = '#2563eb';

const KPI_TONES = {
  'Total Clients': 'is-referrals',
  'Total Deposits': 'is-revenue',
  'Total Earnings': 'is-paid',
  'Pending Earnings': 'is-pending',
  'Loyalty Points': 'is-loyalty',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(
    toAmount(value)
  );

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(toAmount(value));

const formatLots = (value) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(toAmount(value));

const formatPct = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0%';
  return `${num.toFixed(1)}%`;
};

const growthText = (value) => {
  const formatted = formatGrowth(value);
  return formatted ? formatted.replace('↑ ', '').replace('↓ ', '') : null;
};

const growthPrefix = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '↑';
  return num >= 0 ? '↑' : '↓';
};

const SHARE_LINKS = [
  {
    id: 'facebook',
    label: 'Share on Facebook',
    bg: '#1877F2',
    hover: '#0f65d8',
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    path: 'M14.5 8.2h2.4V5.1h-2.4c-2.5 0-4.1 1.6-4.1 4.4V12H8v3h2.4v7.8h3.4V15H16l.6-3h-3.2V9.3c0-.9.5-1.1 1.6-1.1z',
  },
  {
    id: 'x',
    label: 'Share on X',
    bg: '#111827',
    hover: '#000000',
    href: (url) => `https://twitter.com/intent/tweet?url=${url}`,
    path: 'M17.6 4H20l-6.2 7.1L21 20h-5.4l-4.2-5.5L6.4 20H4l6.6-7.6L3.4 4h5.5l3.8 5.1L17.6 4zm-1 14.5h1.5L7.5 5.4H5.9l10.7 13.1z',
  },
  {
    id: 'whatsapp',
    label: 'Share on WhatsApp',
    bg: '#25D366',
    hover: '#1ebe57',
    href: (url) => `https://wa.me/?text=${url}`,
    path: 'M12 3.5a8.5 8.5 0 0 0-7.3 12.8L3.5 20.5l4.4-1.1A8.5 8.5 0 1 0 12 3.5zm0 15.3a6.8 6.8 0 0 1-3.5-1l-.3-.1-2.5.6.7-2.4-.2-.3a6.8 6.8 0 1 1 5.3 3.2zm3.7-4.8c-.2-.1-1.3-.6-1.5-.7-.2-.1-.4-.1-.6.1-.1.1-.5.5-.6.7-.1.1-.2.2-.4.1-.2-.1-.8-.3-1.6-1-.6-.5-1-1.2-1.1-1.4-.1-.2 0-.3.1-.5.1-.1.2-.3.3-.4.1-.1.1-.2.2-.3 0-.1 0-.2-.1-.3 0-.1-.5-1.1-.7-1.5-.2-.3-.4-.4-.7-.4h-.6c-.2 0-.5.1-.6.3-.2.2-.7.7-.7 1.5 0 .9.7 1.8.8 2 .1.2 1.4 2.2 3.5 3 .5.2.9.3 1.2.3.4 0 .8-.1 1.1-.4.3-.3 1.2-1.1 1.4-1.4.2-.3 0-.5 0-.6-.1-.1-.2-.1-.4-.2z',
  },
  {
    id: 'telegram',
    label: 'Share on Telegram',
    bg: '#229ED9',
    hover: '#1b8fc7',
    href: (url) => `https://t.me/share/url?url=${url}`,
    path: 'M19.7 5.1 5.1 10.8c-1 .4-1 1-.1 1.3l3.5 1.1 1.3 4.1c.2.5.4.7.8.7.3 0 .5-.1.7-.4l1.9-1.8 3.9 2.9c.7.4 1.2.2 1.4-.7l2.3-10.8c.2-1-.3-1.5-1.2-1.1zM9 13.1l7.1-4.4c.4-.3.8-.2.5.2l-5.8 5.2-.2 2.1-1.6-3.1z',
  },
];

const DONUT_COLORS = [THEME, REFERRALS_BLUE, '#f59e0b', '#22c55e', '#64748b'];

const PerformanceLineChart = ({ chartData, maxY = 100 }) => {
  const { labels, primary = [], secondary = [] } = chartData;
  const w = 700;
  const h = 240;
  const pad = { l: 44, r: 16, t: 12, b: 36 };
  const cw = w - pad.l - pad.r;
  const ch = h - pad.t - pad.b;
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
    const val = maxY * ratio;
    return Number.isInteger(val) ? val : Number(val.toFixed(1));
  });
  const toY = (v) => pad.t + ch - (v / maxY) * ch;
  const toX = (i) => pad.l + (i / Math.max(labels.length - 1, 1)) * cw;
  const line = (arr) => arr.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');
  const area = (arr) => {
    const pts = arr.map((v, i) => `${toX(i)},${toY(v)}`).join(' ');
    return `${toX(0)},${pad.t + ch} ${pts} ${toX(arr.length - 1)},${pad.t + ch}`;
  };

  const renderDots = (arr, color) =>
    arr.map((v, i) => (
      <circle key={`${color}-${i}`} cx={toX(i)} cy={toY(v)} r="4" fill="#fff" stroke={color} strokeWidth="2" />
    ));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      {yTicks.map((val) => {
        const y = toY(val);
        return (
          <g key={val}>
            <line x1={pad.l} y1={y} x2={pad.l + cw} y2={y} stroke="#f1f5f9" strokeWidth="1" />
            <text x={pad.l - 8} y={y + 4} textAnchor="end" fill="#94a3b8" fontSize="10">
              {val}
            </text>
          </g>
        );
      })}
      {labels.map((m, i) => (
        <text key={`${m}-${i}`} x={toX(i)} y={h - 10} textAnchor="middle" fill="#94a3b8" fontSize="10">
          {m}
        </text>
      ))}
      <defs>
        <linearGradient id="earnFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={THEME} stopOpacity="0.18" />
          <stop offset="100%" stopColor={THEME} stopOpacity="0.02" />
        </linearGradient>
        <linearGradient id="refFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={REFERRALS_BLUE} stopOpacity="0.16" />
          <stop offset="100%" stopColor={REFERRALS_BLUE} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon fill="url(#earnFill)" points={area(primary)} />
      <polygon fill="url(#refFill)" points={area(secondary)} />
      <polyline fill="none" stroke={THEME} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={line(primary)} />
      <polyline fill="none" stroke={REFERRALS_BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" points={line(secondary)} />
      {renderDots(primary, THEME)}
      {renderDots(secondary, REFERRALS_BLUE)}
    </svg>
  );
};

const PerformanceFooterStat = ({ item }) => (
  <div className="dash-perf-stat">
    <div className="dash-perf-stat__icon">
      <PartnerKitIcon name={item.icon} size={18} alt="" />
    </div>
    <div className="min-w-0">
      <p className="dash-perf-stat__label">{item.label}</p>
      <p className="dash-perf-stat__value">{item.value}</p>
      {item.growth != null && Number.isFinite(Number(item.growth)) && (
        <p className="dash-kpi__growth" style={{ marginTop: '0.25rem' }}>
          ↑ {Number(item.growth).toFixed(1)}%
        </p>
      )}
    </div>
  </div>
);

const EarningsDonut = ({ segments, centerValue }) => {
  let offset = 0;
  return (
    <div className="dash-donut">
      <div className="dash-donut__chart">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="38" fill="none" stroke="#f1f5f9" strokeWidth="12" />
          {segments.map((seg, i) => {
            const dash = `${seg.percentage * 2.387} 238.7`;
            const el = (
              <circle
                key={seg.label}
                cx="50"
                cy="50"
                r="38"
                fill="none"
                stroke={DONUT_COLORS[i % DONUT_COLORS.length]}
                strokeWidth="12"
                strokeDasharray={dash}
                strokeDashoffset={-offset}
              />
            );
            offset += seg.percentage * 2.387;
            return el;
          })}
        </svg>
        {centerValue ? (
          <div className="dash-donut__center">
            <p className="dash-donut__center-label">Total</p>
            <p className="dash-donut__center-value">{centerValue}</p>
          </div>
        ) : null}
      </div>
      <div className="dash-donut__legend">
        {segments.map((seg, i) => (
          <div key={seg.label} className="dash-donut__row">
            <div className="dash-donut__row-left">
              <span
                className="dash-donut__dot"
                style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }}
              />
              <span className="dash-donut__name">{seg.label}</span>
            </div>
            <div className="dash-donut__row-right">
              <span className="dash-donut__amount">{seg.amount}</span>
              <span className="dash-donut__pct">{seg.percentage}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SectionLoader = ({ className = 'py-8', size = '28px' }) => (
  <div className={`flex items-center justify-center ${className}`}>
    <NewtonsCradleLoader size={size} />
  </div>
);

const SocialShareButton = ({ label, href, bg, hover, path, onClick }) => {
  const content = (
    <span
      className="ic-share-btn"
      style={{
        '--share-bg': bg,
        '--share-hover': hover || bg,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d={path} fill="#fff" />
      </svg>
    </span>
  );

  if (href) {
    return (
      <a href={href} target="_blank" rel="noreferrer" aria-label={label} title={label}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} aria-label={label} title={label}>
      {content}
    </button>
  );
};

const ActionPromoCard = ({ icon: Icon, title, subtitle, cta, onClick, tone = 'gift' }) => (
  <button type="button" className={`dash-action-card is-${tone}`} onClick={onClick}>
    <span className="dash-action-card__icon">
      <Icon className="w-5 h-5" strokeWidth={2} />
    </span>
    <div className="min-w-0 flex-1 text-left">
      <p className="dash-action-card__title">{title}</p>
      <p className="dash-action-card__sub">{subtitle}</p>
    </div>
    <span className="dash-action-card__cta">{cta}</span>
  </button>
);

const Dashboard = () => {
  const navigate = useNavigate();

  const [widgetsLoading, setWidgetsLoading] = useState(true);
  const [earningLoading, setEarningLoading] = useState(true);
  const [performanceLoading, setPerformanceLoading] = useState(true);
  const [loyaltyLoading, setLoyaltyLoading] = useState(true);
  const [widgets, setWidgets] = useState(null);
  const [earningOverview, setEarningOverview] = useState(null);
  const [performanceOverview, setPerformanceOverview] = useState(null);
  const [loyaltyPoints, setLoyaltyPoints] = useState(null);
  const accountManager = DUMMY_ACCOUNT_MANAGER;
  const managerLoading = false;
  const [tradersLoading, setTradersLoading] = useState(true);
  const [traders, setTraders] = useState([]);
  const [copySuccess, setCopySuccess] = useState(false);

  const loadTraders = useCallback(async ({ cancelled } = { cancelled: () => false }) => {
    setTradersLoading(true);
    try {
      const clientsRes = await apiService.getClientsList({
        pageNumber: 1,
        pageSize: 10,
        searchTerm: '',
        type: '',
        sortBy: '',
        sortOrder: '',
      });
      if (cancelled?.()) return;
      if (clientsRes?.success) {
        setTraders(mapClientsToTraders(extractClientsList(clientsRes)).slice(0, 10));
      } else {
        setTraders([]);
      }
    } catch {
      if (!cancelled?.()) setTraders([]);
    } finally {
      if (!cancelled?.()) setTradersLoading(false);
    }
  }, []);

  const fetchDashboard = useCallback(async ({ cancelled }) => {
    const loadWidgets = async () => {
      setWidgetsLoading(true);
      try {
        const widgetsRes = await apiService.getDashboardWidgets();
        if (cancelled()) return;
        if (widgetsRes?.success) {
          setWidgets(parseDashboardWidgets(widgetsRes.data));
        } else {
          setWidgets(null);
        }
      } catch {
        if (!cancelled()) setWidgets(null);
      } finally {
        if (!cancelled()) setWidgetsLoading(false);
      }
    };

    const loadEarningOverview = async () => {
      setEarningLoading(true);
      try {
        const earningRes = await apiService.getEarningOverview();
        if (cancelled()) return;
        if (earningRes?.success) {
          setEarningOverview(parseEarningOverview(earningRes.data));
        } else {
          setEarningOverview(null);
        }
      } catch {
        if (!cancelled()) setEarningOverview(null);
      } finally {
        if (!cancelled()) setEarningLoading(false);
      }
    };

    const loadPerformanceOverview = async () => {
      setPerformanceLoading(true);
      try {
        const performanceRes = await apiService.getPerformanceOverview();
        if (cancelled()) return;
        if (performanceRes?.success) {
          setPerformanceOverview(parsePerformanceOverview(performanceRes.data));
        } else {
          setPerformanceOverview(null);
        }
      } catch {
        if (!cancelled()) setPerformanceOverview(null);
      } finally {
        if (!cancelled()) setPerformanceLoading(false);
      }
    };

    const loadLoyalty = async () => {
      setLoyaltyLoading(true);
      try {
        const loyaltyRes = await apiService.getLoyaltySummary();
        if (cancelled()) return;
        if (loyaltyRes?.success) {
          const parsed = parseLoyaltySummary(loyaltyRes.data);
          setLoyaltyPoints(parsed.availableLoyaltyPoints ?? parsed.loyaltyPoints ?? 0);
        } else {
          setLoyaltyPoints(null);
        }
      } catch {
        if (!cancelled()) setLoyaltyPoints(null);
      } finally {
        if (!cancelled()) setLoyaltyLoading(false);
      }
    };

    await Promise.all([
      loadWidgets(),
      loadEarningOverview(),
      loadPerformanceOverview(),
      loadLoyalty(),
      loadTraders({ cancelled }),
    ]);
  }, [loadTraders]);

  useFetchOnceByKey('main-dashboard', fetchDashboard, true);

  const referralLink = widgets?.partnerUrl || '';
  const encodedLink = encodeURIComponent(referralLink);

  const stats = useMemo(() => {
    if (!widgets && loyaltyPoints == null) return [];
    return [
      {
        title: 'Total Clients',
        value: formatNumber(widgets?.totalClients ?? widgets?.totalReferrals ?? 0),
        growth: growthText(widgets?.referralsGrowth),
        growthPrefix: growthPrefix(widgets?.referralsGrowth),
        icon: 'statTotalReferrals',
      },
      {
        title: 'Total Earnings',
        value: formatCurrency(widgets?.totalEarning ?? widgets?.paidCommission ?? 0),
        growth: growthText(widgets?.paidGrowth),
        growthPrefix: growthPrefix(widgets?.paidGrowth),
        icon: 'statPaidEarnings',
      },
      {
        title: 'Loyalty Points',
        value: formatNumber(loyaltyPoints ?? 0),
        hint: 'Redeem rewards',
        iconNode: <Award className="w-5 h-5" style={{ color: THEME }} strokeWidth={2} />,
      },
    ];
  }, [widgets, loyaltyPoints]);

  const performanceChart = useMemo(() => {
    const perf = performanceOverview?.chart;
    const earnTrend = earningOverview?.trend;
    const labels = [
      ...new Set([...(earnTrend?.labels || []), ...(perf?.labels || [])]),
    ].filter(Boolean);

    if (!labels.length) return null;

    const earningsByLabel = Object.fromEntries(
      (earnTrend?.labels || []).map((label, i) => [label, toAmount(earnTrend.amounts?.[i])])
    );
    const referralsByLabel = Object.fromEntries(
      (perf?.labels || []).map((label, i) => [label, toAmount(perf.referrals?.[i])])
    );
    const lotsByLabel = Object.fromEntries(
      (perf?.labels || []).map((label, i) => [label, toAmount(perf.lots?.[i])])
    );

    const hasEarningsSeries = (earnTrend?.amounts || []).some((v) => toAmount(v) > 0);

    return {
      labels,
      primary: labels.map((label) =>
        hasEarningsSeries ? earningsByLabel[label] ?? 0 : lotsByLabel[label] ?? 0
      ),
      secondary: labels.map((label) => referralsByLabel[label] ?? 0),
      primaryLabel: hasEarningsSeries ? 'Earnings' : 'Lots',
    };
  }, [performanceOverview, earningOverview]);

  const chartMaxY = useMemo(() => {
    if (!performanceChart) return 100;
    const all = [...performanceChart.primary, ...performanceChart.secondary];
    const peak = Math.max(...all, 1);
    if (peak <= 10) return Math.ceil(peak * 1.15 * 10) / 10;
    return Math.max(10, Math.ceil((peak * 1.15) / 10) * 10);
  }, [performanceChart]);

  const performanceFooter = useMemo(() => {
    const summary = performanceOverview?.summary;
    if (!summary && !widgets) return [];

    const totalReferrals = summary?.totalReferrals ?? widgets?.totalReferrals ?? widgets?.totalClients ?? 0;
    const activeClients = summary?.activeClients ?? widgets?.activeClients ?? 0;
    const newClients = widgets?.newRegistrationsToday ?? 0;
    const clicks = toAmount(widgets?.totalClicks);
    const conversions = toAmount(widgets?.totalConversions);
    let conversionRate = null;
    if (clicks > 0) conversionRate = (conversions / clicks) * 100;
    else if (totalReferrals > 0 && activeClients > 0) {
      conversionRate = (activeClients / totalReferrals) * 100;
    }

    return [
      {
        label: 'Total Referrals',
        value: formatNumber(totalReferrals),
        growth: summary?.referralGrowth ?? widgets?.referralsGrowth,
        icon: 'perfRegistrations',
      },
      {
        label: 'Active Clients',
        value: formatNumber(activeClients),
        icon: 'perfConversion',
      },
      {
        label: 'New Clients',
        value: formatNumber(newClients),
        icon: 'perfClicks',
      },
      {
        label: 'Conversion Rate',
        value: conversionRate != null ? formatPct(conversionRate) : '—',
        growth: widgets?.conversionRateGrowth,
        icon: 'perfConversion',
      },
    ];
  }, [performanceOverview, widgets]);

  const earningsSegments = useMemo(() => {
    const segments = earningOverview?.segments ?? [];
    return segments.map((seg) => ({
      label: seg.label,
      amount: formatCurrency(seg.amount),
      percentage: seg.percentage,
    }));
  }, [earningOverview]);

  const earningsCenterValue = useMemo(() => {
    if (!widgets) return null;
    return formatCurrency(widgets.totalEarning ?? widgets.paidCommission);
  }, [widgets]);

  const earningsSummaryItems = useMemo(() => {
    const summary = earningOverview?.summary;
    const items = [];
    if (summary?.hasData) {
      items.push(
        { label: 'Clients', value: formatNumber(summary.totalClients) },
        { label: 'Lots', value: formatLots(summary.totalLots) },
        { label: 'Profit', value: formatCurrency(summary.totalProfit) }
      );
    }
    if (widgets) {
      items.push({
        label: 'Earnings',
        value: formatCurrency(widgets.totalEarning ?? widgets.paidCommission),
      });
    }
    if (summary?.hasData) {
      items.push({ label: 'Records', value: formatNumber(summary.totalRecords) });
    }
    return items;
  }, [earningOverview, widgets]);

  const fundFigures = useMemo(() => {
    if (!widgets && !performanceOverview?.summary) return [];

    const lots =
      performanceOverview?.summary?.totalLots ??
      widgets?.totalTradingVolume ??
      0;

    return [
      {
        label: 'Available Balance',
        value: formatCurrency(widgets?.availableBalance),
      },
      {
        label: 'Hold Earnings',
        value: formatCurrency(widgets?.holdEarning ?? widgets?.pendingCommission),
      },
      {
        label: 'Withdrawn',
        value: formatCurrency(widgets?.withdrawn),
      },
      {
        label: 'Trading Volume',
        value: formatLots(lots),
        suffix: 'lots',
      },
      {
        label: 'Released',
        value: formatCurrency(widgets?.released),
      },
      {
        label: 'Unreleased',
        value: formatCurrency(widgets?.unreleased),
      },
      {
        label: 'Next Rebate',
        value: formatCurrency(widgets?.nextRebatePayment),
      },
      {
        label: 'Clicks / Conversions',
        value: `${formatNumber(widgets?.totalClicks)} / ${formatNumber(widgets?.totalConversions)}`,
      },
    ];
  }, [widgets, performanceOverview]);

  const handleCopy = async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      setCopySuccess(false);
    }
  };

  const kpiLoading = widgetsLoading || loyaltyLoading;
  const statCardTitles = [
    'Total Clients',
    'Total Earnings',
    'Loyalty Points',
  ];

  return (
    <div className="dash-page px-4 sm:px-6 py-5 space-y-5">
      {/* Referral + Share */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch">
        <div className="xl:col-span-8">
          <div className="dash-card dash-referral dash-referral--hero h-full">
            <div className="dash-referral__hero-grid">
              <div className="min-w-0">
                <p className="dash-referral__label">Grow your network</p>
                <h2 className="dash-referral__title">Your Referral Link</h2>
                <div className="dash-referral__field">
                  {widgetsLoading ? (
                    <SectionLoader className="flex-1 py-4" size="24px" />
                  ) : (
                    <>
                      <span className="dash-referral__link">{referralLink || '—'}</span>
                      <button
                        type="button"
                        onClick={handleCopy}
                        disabled={!referralLink}
                        className="dash-referral__copy"
                        aria-label="Copy link"
                      >
                        <Copy className="w-4 h-4" strokeWidth={2.25} />
                        Copy
                      </button>
                    </>
                  )}
                </div>
                {copySuccess && <p className="dash-referral__ok">Copied to clipboard!</p>}
              </div>
              <div className="dash-referral__art">
                <PartnerKitIcon
                  name="referralGiftIllustration"
                  size={150}
                  alt="Referral rewards"
                  className="object-contain"
                  style={{ width: 150, height: 126 }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="xl:col-span-4">
          <div className="dash-card dash-share h-full">
            <p className="dash-referral__share-label">Share via</p>
            <div className="dash-share__icons">
              {SHARE_LINKS.map((item) => (
                <SocialShareButton
                  key={item.id}
                  label={item.label}
                  href={referralLink ? item.href(encodedLink) : undefined}
                  bg={item.bg}
                  hover={item.hover}
                  path={item.path}
                />
              ))}
            </div>
            <p className="dash-share__hint">Share your link and grow your partner network faster.</p>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      {kpiLoading ? (
        <div className="dash-kpi-grid dash-kpi-grid--5">
          {statCardTitles.map((title) => (
            <div key={title} className={`dash-card dash-kpi ${KPI_TONES[title] || ''}`}>
              <div className="dash-kpi__top">
                <div>
                  <p className="dash-kpi__label">{title}</p>
                  <SectionLoader className="py-3 justify-start" size="22px" />
                </div>
                <div className="dash-kpi__icon" />
              </div>
            </div>
          ))}
        </div>
      ) : stats.length > 0 ? (
        <div className="dash-kpi-grid dash-kpi-grid--5">
          {stats.map((item) => (
            <div key={item.title} className={`dash-card dash-kpi ${KPI_TONES[item.title] || ''}`}>
              <div className="dash-kpi__top">
                <div className="min-w-0 flex-1">
                  <p className="dash-kpi__label">{item.title}</p>
                  <p className="dash-kpi__value">{item.value}</p>
                  {item.growth != null ? (
                    <span className="dash-kpi__growth">
                      {item.growthPrefix} {item.growth}
                    </span>
                  ) : item.hint ? (
                    <span className="dash-kpi__hint">{item.hint}</span>
                  ) : null}
                </div>
                <div className="dash-kpi__icon">
                  {item.iconNode || <PartnerKitIcon name={item.icon} size={26} alt="" />}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="dash-card dash-empty">No dashboard data available</div>
      )}

      {/* Performance + Earnings Snapshot */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch">
        <div className="xl:col-span-8">
          <div className="dash-card p-5 sm:p-6 h-full">
            <div className="dash-card__head">
              <h2 className="dash-card__title">Performance Overview</h2>
              <button type="button" className="dash-card__chip">
                Last 30 Days <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="dash-legend">
              <span className="dash-legend__item">
                <span className="dash-legend__dot" style={{ backgroundColor: THEME }} />
                {performanceChart?.primaryLabel || 'Earnings'}
              </span>
              <span className="dash-legend__item">
                <span className="dash-legend__dot" style={{ backgroundColor: REFERRALS_BLUE }} />
                Referrals
              </span>
            </div>
            <div className="h-[240px]">
              {performanceLoading || earningLoading ? (
                <SectionLoader className="h-full" />
              ) : performanceChart ? (
                <PerformanceLineChart chartData={performanceChart} maxY={chartMaxY} />
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-gray-400">
                  No performance data available
                </div>
              )}
            </div>
            {!performanceLoading && performanceFooter.length > 0 && (
              <div className="dash-perf-footer dash-perf-footer--4">
                {performanceFooter.map((item) => (
                  <PerformanceFooterStat key={item.label} item={item} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="xl:col-span-4">
          <div className="dash-card p-5 sm:p-6 h-full flex flex-col">
            <div className="dash-card__head">
              <h2 className="dash-card__title">Earnings Snapshot</h2>
            </div>
            <div className="flex-1 flex items-center">
              {earningLoading ? (
                <SectionLoader className="w-full py-10" />
              ) : earningsSegments.length > 0 ? (
                <EarningsDonut segments={earningsSegments} centerValue={earningsCenterValue} />
              ) : (
                <div className="w-full dash-empty" style={{ padding: '1.5rem 1rem' }}>
                  No earnings breakdown available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Earnings Overview + side support */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-start">
        <div className="xl:col-span-8 space-y-4">
          <div className="dash-card dash-earn-strip p-5 sm:p-6">
            <div className="dash-card__head">
              <h2 className="dash-card__title">Earnings Overview</h2>
              <button
                type="button"
                className="dash-earn-link"
                onClick={() => navigate('/earnings/dashboard')}
              >
                View Detailed Report
              </button>
            </div>
            {earningLoading || widgetsLoading ? (
              <SectionLoader className="py-8" />
            ) : earningsSummaryItems.length > 0 ? (
              <div className="dash-earn__summary">
                {earningsSummaryItems.map((item) => (
                  <div key={item.label} className="dash-earn__stat">
                    <p className="dash-earn__stat-label">{item.label}</p>
                    <p className="dash-earn__stat-value">{item.value}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="dash-empty" style={{ padding: '1.5rem 1rem' }}>
                No earnings data available
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ActionPromoCard
              icon={Gift}
              title="Promotions & Campaigns"
              subtitle="Boost conversions with active offers"
              cta="Explore Now"
              tone="gift"
              onClick={() => navigate('/promotions/list')}
            />
            <ActionPromoCard
              icon={Trophy}
              title="Rewards Center"
              subtitle="Claim milestones and loyalty rewards"
              cta="View Rewards"
              tone="trophy"
              onClick={() => navigate('/rewards/dashboard')}
            />
          </div>

          <div className="dash-card dash-figures p-5 sm:p-6">
            <div className="dash-card__head">
              <div className="flex items-center gap-2.5">
                <span className="dash-figures__icon">
                  <Wallet className="w-4 h-4" strokeWidth={2.5} />
                </span>
                <div>
                  <h2 className="dash-card__title">Fund & Volume Figures</h2>
                  <p className="dash-figures__sub">Live balances, rebate and traffic metrics</p>
                </div>
              </div>
              <button
                type="button"
                className="dash-earn-link"
                onClick={() => navigate('/payout-history')}
              >
                View Payouts
              </button>
            </div>
            {widgetsLoading || performanceLoading ? (
              <SectionLoader className="py-10" />
            ) : fundFigures.length > 0 ? (
              <div className="dash-figures__grid">
                {fundFigures.map((item) => (
                  <div key={item.label} className="dash-figures__item">
                    <p className="dash-figures__label">{item.label}</p>
                    <p className="dash-figures__value">
                      {item.value}
                      {item.suffix ? (
                        <span className="dash-figures__suffix">{item.suffix}</span>
                      ) : null}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="dash-empty" style={{ padding: '1.5rem 1rem' }}>
                No figures available
              </div>
            )}
          </div>
        </div>

        <div className="xl:col-span-4 space-y-4">
          <DashboardAccountManager manager={accountManager} loading={managerLoading} />
          <DashboardTraders
            traders={traders}
            loading={tradersLoading}
            onRefresh={() => loadTraders()}
          />

          <div className="dash-card dash-help">
            <div className="dash-help__body">
              <span className="dash-help__icon">
                <MessageCircle className="w-5 h-5" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <p className="dash-help__title">Need Help?</p>
                <p className="dash-help__sub">Chat with our support team anytime.</p>
              </div>
            </div>
            <button type="button" className="dash-help__cta" onClick={() => navigate('/support')}>
              Chat Now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
